import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import {
  appendLocalAdaptiveEvent,
  computeAdaptiveProfileFromEvents,
  mergeActivityEvents,
  readLocalEvents,
  type AdaptiveProfile,
  type ActivityEvent,
} from '../lib/adaptive-experience';
import { fetchAdaptiveExperienceEvents, pushAdaptiveExperienceEvents } from '../lib/adaptive-experience-api';
import {
  enqueueAdaptiveSyncFailed,
  flushAdaptiveSyncQueue,
} from '../lib/adaptive-sync-queue';

type SyncState = 'idle' | 'loading' | 'error';
const ADAPTIVE_REFRESH_MIN_INTERVAL_MS = 120_000;
const shouldSurfaceOfflineSyncError = () =>
  typeof navigator === 'undefined' ? true : !navigator.onLine;

type Ctx = {
  profile: AdaptiveProfile;
  refresh: () => void;
  recordActivity: (type: string, meta?: Record<string, unknown>) => void;
  syncState: SyncState;
  mergedEventCount: number;
};

const AdaptiveExperienceContext = createContext<Ctx | null>(null);

export function AdaptiveExperienceProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();
  const [localVersion, setLocalVersion] = useState(0);
  const [serverEvents, setServerEvents] = useState<ActivityEvent[]>([]);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const syncErrorToastShown = useRef(false);
  const lastServerLoadRef = useRef(0);
  const loadInProgressRef = useRef(false);

  const mergedEvents = useMemo(() => {
    void localVersion;
    return mergeActivityEvents(readLocalEvents(), serverEvents);
  }, [localVersion, serverEvents]);

  const profile = useMemo(() => computeAdaptiveProfileFromEvents(mergedEvents), [mergedEvents]);

  useEffect(() => {
    if (syncState === 'error' && !syncErrorToastShown.current && shouldSurfaceOfflineSyncError()) {
      syncErrorToastShown.current = true;
      toast.message('Personalization sync paused', {
        description: 'Your preferences still work on this device. We will retry when you are back online.',
        duration: 6500,
      });
    }
    if (syncState === 'idle') {
      syncErrorToastShown.current = false;
    }
  }, [syncState]);

  const loadServer = useCallback(async (options?: { force?: boolean }) => {
    if (!accessToken) {
      setServerEvents([]);
      setSyncState('idle');
      return;
    }
    const force = !!options?.force;
    const now = Date.now();
    if (!force) {
      if (loadInProgressRef.current) return;
      if (lastServerLoadRef.current > 0 && now - lastServerLoadRef.current < ADAPTIVE_REFRESH_MIN_INTERVAL_MS) {
        return;
      }
    }

    loadInProgressRef.current = true;
    setSyncState('loading');
    try {
      await flushAdaptiveSyncQueue(accessToken);

      const remote = await fetchAdaptiveExperienceEvents(accessToken, 200);
      const remoteIds = new Set(
        remote.map((e) => e.clientEventId).filter((id): id is string => typeof id === 'string' && id.length > 0),
      );
      const local = readLocalEvents();
      const toPush = local.filter((e) => e.clientEventId && !remoteIds.has(e.clientEventId));

      if (toPush.length > 0) {
        try {
          await pushAdaptiveExperienceEvents(accessToken, toPush);
        } catch {
          enqueueAdaptiveSyncFailed(toPush);
          setServerEvents(remote);
          setSyncState(shouldSurfaceOfflineSyncError() ? 'error' : 'idle');
          return;
        }
        try {
          const remoteAfter = await fetchAdaptiveExperienceEvents(accessToken, 200);
          setServerEvents(remoteAfter);
        } catch {
          setServerEvents(remote);
          setSyncState(shouldSurfaceOfflineSyncError() ? 'error' : 'idle');
          return;
        }
      } else {
        setServerEvents(remote);
      }
      lastServerLoadRef.current = Date.now();
      setSyncState('idle');
    } catch (error) {
      const message = String((error as Error | undefined)?.message || '').toLowerCase();
      const isAuthError =
        message.includes('unauthorized') ||
        message.includes('forbidden') ||
        message.includes('401') ||
        message.includes('403');
      if (isAuthError) {
        // Treat auth drift as non-fatal so the UI keeps local personalization active
        // while session refresh/re-auth flows recover in the background.
        setSyncState('idle');
        return;
      }
      setSyncState(shouldSurfaceOfflineSyncError() ? 'error' : 'idle');
      setServerEvents([]);
    } finally {
      loadInProgressRef.current = false;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setServerEvents([]);
      setSyncState('idle');
      return;
    }
    // Defer non-critical personalization sync until after initial route paint.
    const timer = window.setTimeout(() => {
      void loadServer({ force: true });
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [accessToken, loadServer]);

  useEffect(() => {
    if (!accessToken) return;
    const runFlush = () => {
      void flushAdaptiveSyncQueue(accessToken).then((n) => {
        if (n > 0) setLocalVersion((v) => v + 1);
      });
    };
    runFlush();
    const onOnline = () => runFlush();
    window.addEventListener('online', onOnline);

    let removeApp: (() => void) | undefined;
    let cancelled = false;
    void import('@capacitor/app')
      .then(({ App }) => {
        if (cancelled) return;
        void App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) runFlush();
        }).then((h) => {
          removeApp = () => void h.remove();
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      removeApp?.();
    };
  }, [accessToken]);

  const refresh = useCallback(() => {
    setLocalVersion((v) => v + 1);
    void loadServer({ force: true });
  }, [loadServer]);

  useEffect(() => {
    const onFocus = () => {
      void loadServer({ force: false });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadServer]);

  const recordActivity = useCallback(
    (type: string, meta?: Record<string, unknown>) => {
      const ev = appendLocalAdaptiveEvent(type, meta);
      setLocalVersion((v) => v + 1);
      if (accessToken && ev.clientEventId) {
        void pushAdaptiveExperienceEvents(accessToken, [ev]).catch(() => {
          enqueueAdaptiveSyncFailed([ev]);
        });
      }
    },
    [accessToken],
  );

  const value = useMemo<Ctx>(
    () => ({
      profile,
      refresh,
      recordActivity,
      syncState,
      mergedEventCount: mergedEvents.length,
    }),
    [profile, refresh, recordActivity, syncState, mergedEvents.length],
  );

  return <AdaptiveExperienceContext.Provider value={value}>{children}</AdaptiveExperienceContext.Provider>;
}

export function useAdaptiveExperience(): Ctx {
  const ctx = useContext(AdaptiveExperienceContext);
  if (!ctx) {
    throw new Error('useAdaptiveExperience must be used within AdaptiveExperienceProvider');
  }
  return ctx;
}

export function useAdaptiveExperienceOptional(): Ctx | null {
  return useContext(AdaptiveExperienceContext);
}
